import { useRef } from 'react';
import { useDatePickerState } from 'react-stately';
import { AriaDatePickerProps, useDatePicker, useLocale } from 'react-aria';
import {
  CalendarIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  CalendarDate,
  CalendarDateTime,
  DateValue,
  ZonedDateTime,
} from '@internationalized/date';
import { FieldButton } from './Button';
import Calendar from '../Calendar';
import { DateField, TimeField } from '../Calendar/components/DateField';
import Popover from '../Popover';
import Dialog from '../Dialog';
import { Modal } from '../Modal';

export interface DatePickerProps extends AriaDatePickerProps<DateValue> {
  /** Label of the field, required for accessibility */
  label: string;
  /** If you want to use a custom styled label */
  hideLabel?: boolean;
  /** What's the minimum value that can be picked? */
  minValue?: CalendarDateTime | CalendarDate | ZonedDateTime;
  /** What's the default value of the field?
   * If no value was given, then it will show a placeholder with the user locale date format
   */
  defaultValue?: CalendarDateTime | CalendarDate | ZonedDateTime;
  /** A function that will have the picked date as an argument
   */
  setDate?: (s: CalendarDateTime | CalendarDate | ZonedDateTime) => void;
}

export function DatePicker({ hideLabel, ...props }: DatePickerProps) {
  const { locale } = useLocale();
  const state = useDatePickerState({
    ...props,
    onChange: (value) => {
      if (props.setDate && value)
        props.setDate(value);
    },
    shouldCloseOnSelect: false,
  });
  const ref = useRef(null);

  const {
    groupProps,
    labelProps,
    fieldProps,
    buttonProps,
    dialogProps,
    calendarProps,
  } = useDatePicker(props, state, ref);
  return (
    <div className="relative w-full inline-flex flex-col text-left">
      <span
        {...labelProps}
        className={`text-sm font-medium leading-6 text-gray-900 mb-2 ${
          hideLabel ? 'hidden' : ''
        }`}
      >
        {props.label}
      </span>

      <div {...groupProps} ref={ref} className="flex group ">
        <div className="bg-white border border-gray-300 w-full group-hover:border-gray-400 transition-colors rounded-l-md pl-4 py-1.5 pr-10 group-focus-within:border-primary-600 group-focus-within:group-hover:border-primary-600 relative flex items-center">
          <DateField {...fieldProps} />
          {state.validationState === 'invalid' && (
            <ExclamationCircleIcon className="w-6 h-6 text-red-500 absolute right-1" />
          )}
        </div>
        <FieldButton {...buttonProps} isPressed={state.isOpen}>
          <CalendarIcon className="w-5 h-5 text-gray-700 group-focus-within:text-primary-700" />
        </FieldButton>
      </div>

      <Modal
        show={state.isOpen}
        onClose={state.close}
        className="!sm:w-fit !w-fit"
      >
        <Dialog {...dialogProps}>
          <Calendar {...calendarProps} />
          <TimeField
            locale={locale}
            label="Time"
            value={state.timeValue}
            onChange={state.setTimeValue}
          />
        </Dialog>
      </Modal>
    </div>
  );
}
